/**
 * This module provides the BatterySettings higher order component.
 *
 * It defines the layout of the settings sections.
 *
 * And it prepares the consumed form values. The injected form values will be updated as soon as an update request was
 * initiated. This is required to trigger the reactivity behavior of Formik later on.
 *
 */

import React, { useState } from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { LoadingPrioritizationFormData } from 'src/Libs/wallboxes'
import SettingsFormContainer from 'src/Elements/SystemSettings/forms/SettingsFormContainer'
import { LoadingPriorisationFormModel, LoadingPriorisationModel } from 'src/Libs/wallboxes/models'
import { loadPrioToForm } from 'src/Libs/wallboxes/transformers/load-prio'
import { makeStyles } from 'tss-react/mui'
import FormikWithExitHandler from 'src/Libs/form/components/FormikWithExitHandler'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'

const useStyles = makeStyles()(theme => ({
  contentAccordion: {
    padding: theme.spacing(0),
  },
  headerContent: {
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(4),
    },
  },
}))

interface Props {
  model: LoadingPriorisationModel
  title: string
  onUpdate: (values: LoadingPriorisationFormModel) => Promise<void>
  hasWritingAccess?: boolean
  disabled: boolean
}

const WallboxLoadingPrioAccordion: React.VFC<Props> = ({ title, model, onUpdate, hasWritingAccess = true, disabled = false }) => {
  const [formValues, setFormValues] = useState(loadPrioToForm(model))
  const { classes } = useStyles()
  const { triggerAppRating } = useAppRatingContext()

  const handleSubmit = async (values: LoadingPriorisationFormModel): Promise<void> => {
    try {
      await onUpdate(values)
      triggerAppRating()
    } catch (error) {
      /* revert changes */
      setFormValues({ ...formValues })
    }
  }

  return (
    <FormikWithExitHandler formKey="wallbox-loading-prio-form" initialValues={formValues} onSubmit={handleSubmit}>
      <AccordionItem title={title} disabled={disabled} contentClassName={classes.contentAccordion} headerContentClassName={classes.headerContent}>
        <SettingsFormContainer model={formValues}>
          <LoadingPrioritizationFormData hasWritingAccess={hasWritingAccess} />
        </SettingsFormContainer>
      </AccordionItem>
    </FormikWithExitHandler>
  )
}

export default WallboxLoadingPrioAccordion
